import { Field, useFormikContext } from 'formik';
import { useState } from 'react';

import {
  AutoCompleteSelectServiceUI,
  AutoCompleteSelectUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
} from '../Interface';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { InvManagementMethod } from '../../config/itemGroup';
import masterDataService from '../../service/masterData.service';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';

const normalizeSingleItemResponse = (response) => {
  if (Array.isArray(response?.body)) {
    return response.body[0] ?? null;
  }

  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response?.body ?? response ?? null;
};

export const AssemblyDefinitionFields = ({ viewField, selectedValues, autoCompleteOptions, autoCompleteData }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const [itemFieldResetKey, setItemFieldResetKey] = useState(0);

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) {
      return null;
    }

    const currentFormItem = values?.item;
    if (currentFormItem?.itemCode === itemCode) {
      return currentFormItem;
    }

    const selectedItem = selectedValues?.item;
    if (selectedItem?.itemCode === itemCode) {
      return selectedItem;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      return normalizeSingleItemResponse(response);
    } catch {
      return null;
    }
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="col-span-4 md:col-span-2">
        <Field
          key={`assembly-head-item-${itemFieldResetKey}`}
          type="autocomplete"
          name="itemCode"
          component={AutoCompleteSelectServiceUI}
          fullWidth
          label={<FormLabelUI text="Termék" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          service={itemAutocompleteService}
          serviceRequestBuilder={itemAutocompleteRequestBuilder}
          onChange={async (_e, newVal) => {
            const selectedItemCode = newVal?.value ?? null;
            const selectedItem = await getItemMetaByCode(selectedItemCode);
            if (selectedItem?.invManagementMethod === InvManagementMethod.BATCH) {
              await setFieldValue('itemCode', null);
              await setFieldValue('item', null);
              setItemFieldResetKey((prev) => prev + 1);
              addNotification({
                content: 'Sarzsos cikk nem választható az összeállítás fejébe!',
                type: NotificationType.ERROR,
              });
              await validateField('itemCode');
              return;
            }

            await setFieldValue('itemCode', selectedItemCode);
            await validateField('itemCode');
            setFieldValue('item', selectedItem ?? null);
            setItemFieldResetKey(0);
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.itemCode) }}
          selectedValue={values.itemCode}
          selectedLabelValue={
            values.itemCode
              ? [values.itemCode, values?.item?.itemName ?? values?.item?.name].filter(Boolean).join(' - ')
              : ''
          }
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="sourcePriceList"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Forrás árlista" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.sourcePriceList} />}
          onChange={(_e, newVal) => {
            setFieldValue('sourcePriceList', newVal?.value ?? null).then(() => {
              validateField('sourcePriceList');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.sourcePriceList) }}
          selectedValue={values.sourcePriceList}
          selectedLabelValue={autoCompleteData?.priceList?.find((p) => p.id === values.sourcePriceList)?.name ?? ''}
          dataset={autoCompleteOptions.priceList}
        />
      </div>
      <div className="col-span-4">
        <Field
          type="text"
          name="note"
          as={InputUI}
          fullWidth
          multiline
          minRows={2}
          label={<FormLabelUI text="Megjegyzés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.note} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.note !== '' && values.note !== null }}
        />
      </div>
    </div>
  );
};
